import { type ButtonV3Props, LayoutGrid } from '@jouzen/ecom-components';
import { cx } from 'class-variance-authority';
import type { MessageKey } from 'types/LocaleMessageKey';

import type { ImageProps } from '../Image';
import FooterCtaContent from './FooterCtaContent';
import FooterCtaImage from './FooterCtaImage';
import type { FooterCtaVariant } from './types';

interface FooterCtaButton extends Omit<ButtonV3Props, 'children'> {
  readonly label: MessageKey;
}

export interface FooterCtaProps {
  readonly bulletPoints?: readonly MessageKey[] | undefined;
  readonly button: FooterCtaButton;
  readonly className?: string;
  readonly description?: MessageKey | undefined;
  readonly eyebrow?: MessageKey | undefined;
  readonly image: Pick<
    ImageProps,
    'alt' | 'localizedAlt' | 'localizedSrc' | 'src'
  >;
  readonly imageClassName?: string | undefined;
  readonly indentation?: string | undefined;
  readonly multiline?: boolean | undefined;
  readonly title: MessageKey;
  readonly variant: {
    sm: FooterCtaVariant;
    lg: FooterCtaVariant;
  };
  readonly inverted?: boolean;
}

const FooterCta = ({
  bulletPoints = [],
  button,
  className,
  description,
  eyebrow,
  image,
  imageClassName,
  inverted = false,
  indentation,
  multiline,
  title,
  variant,
}: FooterCtaProps) => {
  return (
    <section
      className={cx(className, {
        'text-sandstone-200': variant.sm === 'background',
        'lg:text-sandstone-200': variant.lg === 'background',
      })}
      data-cy="footer-cta"
    >
      <LayoutGrid
        className={cx({
          'md:items-center': variant.lg === 'two_column',
        })}
      >
        <FooterCtaImage
          image={image}
          variant={variant}
          className={imageClassName}
        />
        <FooterCtaContent
          bulletPoints={bulletPoints}
          button={button}
          description={description}
          eyebrow={eyebrow}
          indentation={indentation}
          inverted={inverted}
          multiline={multiline}
          title={title}
          variant={variant}
        />
      </LayoutGrid>
    </section>
  );
};

export default FooterCta;
